import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import cx from "clsx"
import { formatDistanceToNow } from "date-fns"
import { format, toZonedTime } from "date-fns-tz"
import type { KioskDeviceSummary } from "shared"
import { fetchKioskDevices, revokeKioskDevice, deleteKioskDevice } from "../api"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { Tag } from "@rawa/ui/Tag"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./KioskDevicesListPage.module.scss"

// Perth time with the zone labelled rather than trust the viewer's locale.
const TZ = "Australia/Perth"

function formatDate(iso: string): string {
  return format(toZonedTime(new Date(iso), TZ), "d MMM yyyy, h:mm aaa (zzz)", {
    timeZone: TZ,
  })
}

// A device has no name, so the site plus a short id is its identity. The id is
// truncated at the UUID's first group: enough to tell two tablets at one site
// apart, to say out loud, and to match with `where id::text like '…%'` while psql
// is still the only other way to inspect a device.
const shortId = (device: KioskDeviceSummary) => device.id.slice(0, 8)

// The same length as the short id: enough to see two records share a tablet.
const shortFingerprint = (fingerprint: string) => fingerprint.slice(0, 8)

const label = (device: KioskDeviceSummary) =>
  device.locations
    .map((location) => location.name ?? "Unknown location")
    .join(", ")

/**
 * Active first, then location, so a site's tablets are always adjacent. That
 * ordering is the point rather than a nicety: there is no device name (ADR-025),
 * so when two tablets sit at one site the only way to tell them apart is to
 * compare their last-seen times — which is impossible if sorting by recency has
 * put other sites between them.
 *
 * Within a location: most recently used first.
 */
function forReview(a: KioskDeviceSummary, b: KioskDeviceSummary) {
  if (a.status !== b.status) return a.status === "active" ? -1 : 1
  const byLocation = label(a).localeCompare(label(b))
  if (byLocation !== 0) return byLocation
  return (b.lastSeenAt ?? "").localeCompare(a.lastSeenAt ?? "")
}

function DeviceAction({
  name,
  pending,
  consequence,
  confirming,
  working,
  onAsk,
  onCancel,
  onConfirm,
}: {
  name: string
  pending: string
  consequence: string
  confirming: boolean
  working: boolean
  onAsk: () => void
  onCancel: () => void
  onConfirm: () => void
}) {
  if (!confirming) {
    return (
      <button type="button" className={styles.action} onClick={onAsk}>
        {name}
      </button>
    )
  }

  return (
    <div className={styles.confirm}>
      <p className={styles.confirmText}>{consequence}</p>
      <div className={styles.confirmActions}>
        <button
          type="button"
          className={styles.action}
          onClick={onCancel}
          disabled={working}
        >
          Cancel
        </button>
        <button
          type="button"
          className={cx(styles.action, styles.actionDanger)}
          onClick={onConfirm}
          disabled={working}
        >
          {working ? pending : `${name} device`}
        </button>
      </div>
    </div>
  )
}

// What an action that failed says, in the place the list's own load error goes.
const actionFailure = (err: Error, action: "revoke" | "remove") =>
  isForbidden(err)
    ? NOT_AUTHORIZED_MESSAGE
    : `Could not ${action} this device. Please try again.`

export function KioskDevicesListPage() {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<string | null>(null)

  // Read each time the page is shown and never in the background.
  const {
    data: devices = [],
    isPending: loading,
    isSuccess: loaded,
    isError,
    error: loadFailure,
  } = useQuery({
    queryKey: ["kiosk-devices"],
    queryFn: fetchKioskDevices,
    staleTime: Infinity,
    refetchOnMount: "always",
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchKioskDevices", forbiddenIsExpected: true },
  })

  // Left on screen and still revocable when it fails: reporting success for a
  // revocation that did not happen is the worst failure this page has.
  const revoke = useMutation({
    mutationFn: revokeKioskDevice,
    onSuccess: (_, id) =>
      queryClient.setQueryData<KioskDeviceSummary[]>(
        ["kiosk-devices"],
        (held) =>
          held?.map((device) =>
            device.id === id ? { ...device, status: "revoked" } : device,
          ),
      ),
    onError: (err) => {
      if (!isForbidden(err)) trackError(err, { source: "revokeKioskDevice" })
    },
    onSettled: () => setConfirming(null),
  })

  const remove = useMutation({
    mutationFn: deleteKioskDevice,
    onSuccess: (_, id) =>
      queryClient.setQueryData<KioskDeviceSummary[]>(
        ["kiosk-devices"],
        (held) => held?.filter((device) => device.id !== id),
      ),
    onError: (err) => {
      if (!isForbidden(err)) trackError(err, { source: "deleteKioskDevice" })
    },
    onSettled: () => setConfirming(null),
  })

  const working = revoke.isPending || remove.isPending
  const error = isError
    ? isForbidden(loadFailure)
      ? NOT_AUTHORIZED_MESSAGE
      : "Could not load kiosk devices. Please try again."
    : revoke.isError
      ? actionFailure(revoke.error, "revoke")
      : remove.isError
        ? actionFailure(remove.error, "remove")
        : null

  // Starting either action clears what the other last said; an action's own
  // error clears when it is tried again.
  function handleRevoke(id: string) {
    remove.reset()
    revoke.mutate(id)
  }

  function handleRemove(id: string) {
    revoke.reset()
    remove.mutate(id)
  }

  const sorted = [...devices].sort(forReview)
  const revoked = devices.filter((d) => d.status === "revoked").length

  return (
    <div className={styles.page}>
      <h1 className={cx(typography.h2, styles.heading)}>Kiosk devices</h1>
      <p className={styles.subtitle}>
        Tablets set up to collect feedback, the ones in service first. Revoking
        one stops it serving clients on its next request; a revoked tablet can
        then be removed from this list.
      </p>

      {loading && (
        <div role="status" aria-label="Loading devices" className={styles.list}>
          <span className={styles.skeletonCard} />
          <span className={styles.skeletonCard} />
        </div>
      )}

      {error && <ErrorCallout>{error}</ErrorCallout>}

      {loaded && devices.length === 0 && (
        <p className={styles.empty}>
          No kiosks are set up yet. A staff member enrols a tablet by signing in
          on it and choosing its location.
        </p>
      )}

      {sorted.length > 0 && (
        <section className={styles.section}>
          <h2 className={cx(typography.h5, styles.sectionHeading)}>
            Enrolled ({devices.length}
            {revoked > 0 && `, ${revoked} revoked`})
          </h2>

          <ul aria-label="Enrolled devices" className={styles.list}>
            {sorted.map((device) => (
              <li key={device.id} className={styles.card}>
                <div className={styles.body}>
                  <Tag
                    colour={device.status === "active" ? "mint" : "disabled"}
                    className={styles.badge}
                  >
                    {device.status === "active" ? "Active" : "Revoked"}
                  </Tag>

                  <div className={styles.info}>
                    <span className={styles.location}>
                      {label(device)}{" "}
                      <span className={styles.shortId}>
                        ({shortId(device)}…)
                      </span>
                    </span>
                    <span className={styles.meta}>
                      {device.lastSeenAt ? (
                        <span title={formatDate(device.lastSeenAt)}>
                          Last seen{" "}
                          {formatDistanceToNow(new Date(device.lastSeenAt))} ago
                        </span>
                      ) : (
                        <span>Never used</span>
                      )}
                      {" · "}
                      <span title={formatDate(device.enrolledAt)}>
                        set up{" "}
                        {formatDistanceToNow(new Date(device.enrolledAt))} ago
                      </span>
                      {device.fingerprint && (
                        <>
                          {" · "}
                          <span title={device.fingerprint}>
                            fingerprint {shortFingerprint(device.fingerprint)}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </div>

                <DeviceAction
                  {...(device.status === "active"
                    ? {
                        name: "Revoke",
                        pending: "Revoking…",
                        consequence:
                          "This tablet will stop collecting feedback until a staff member sets it up again.",
                        onConfirm: () => handleRevoke(device.id),
                      }
                    : {
                        name: "Remove",
                        pending: "Removing…",
                        consequence:
                          "This tablet will disappear from the list. Its enrolment will still be on record.",
                        onConfirm: () => handleRemove(device.id),
                      })}
                  confirming={confirming === device.id}
                  working={working}
                  onAsk={() => setConfirming(device.id)}
                  onCancel={() => setConfirming(null)}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
